import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import Autocomplete from "@material-ui/lab/Autocomplete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ClienteQuickCreateModal from "../ClienteQuickCreateModal";

// Modal para vincular um contato (Contact) a um cliente ERP.
// A busca do GET /clientes cobre razao social (NOMECLI), nome fantasia
// (NOMEFANTASIA) e CNPJ (CGCCLI, com ou sem mascara).
const LinkClienteModal = ({ open, onClose, contact }) => {
	const [searchParam, setSearchParam] = useState("");
	const [options, setOptions] = useState([]);
	const [selectedCliente, setSelectedCliente] = useState(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [quickCreateOpen, setQuickCreateOpen] = useState(false);

	useEffect(() => {
		if (!open) {
			setSearchParam("");
			setOptions([]);
			setSelectedCliente(null);
			setSaving(false);
			setQuickCreateOpen(false);
			return;
		}

		let active = true;
		setLoading(true);
		const delayDebounceFn = setTimeout(async () => {
			try {
				const { data } = await api.get("/clientes", {
					params: { searchParam, pageNumber: 1 }
				});
				if (active) {
					setOptions(data?.clientes || []);
				}
			} catch (err) {
				toastError(err);
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		}, 350);

		return () => {
			active = false;
			clearTimeout(delayDebounceFn);
		};
	}, [open, searchParam]);

	const getClienteLabel = option =>
		[option?.NOMEFANTASIA, option?.NOMECLI].filter(Boolean).join(" - ");

	// O cadastro rapido nao se fecha sozinho no sucesso - quem fecha e este
	// handler. O cliente novo entra nas opcoes e vira a selecao, mas o vinculo
	// so acontece quando o usuario confirma no botao "Vincular".
	const handleClienteCreated = novoCliente => {
		setQuickCreateOpen(false);
		if (!novoCliente?.CODCLI) return;

		setOptions(current =>
			current.some(c => String(c.CODCLI) === String(novoCliente.CODCLI))
				? current
				: [novoCliente, ...current]
		);
		setSelectedCliente(novoCliente);
	};

	const handleLink = async () => {
		if (!contact?.id || !selectedCliente?.CODCLI || saving) {
			return;
		}

		setSaving(true);
		try {
			const { data } = await api.post(
				`/contacts/${contact.id}/link-cliente`,
				{ clienteId: selectedCliente.CODCLI }
			);

			if (data?.alreadyLinked) {
				toast.info(i18n.t("linkClienteModal.alreadyLinked"));
			} else {
				toast.success(i18n.t("linkClienteModal.success"));
			}
			onClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{i18n.t("linkClienteModal.title")}</DialogTitle>
			<DialogContent>
				<Typography
					variant="body2"
					color="textSecondary"
					style={{ marginBottom: 16 }}
				>
					{i18n.t("linkClienteModal.contact")}: {contact?.name || "-"}
				</Typography>

				<Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
					<Box flex={1}>
						<Autocomplete
							options={options}
							loading={loading}
							value={selectedCliente}
							onChange={(event, value) => setSelectedCliente(value)}
							// A busca e feita no servidor (inclui CNPJ, que nao esta no label);
							// sem isso o Autocomplete refiltra pelo label e descarta os resultados
							filterOptions={opts => opts}
							getOptionLabel={getClienteLabel}
							getOptionSelected={(option, value) => option.CODCLI === value?.CODCLI}
							renderOption={option => (
								<div>
									<Typography variant="body2">{getClienteLabel(option)}</Typography>
									{option?.CGCCLI && (
										<Typography variant="caption" color="textSecondary">
											CNPJ: {option.CGCCLI}
										</Typography>
									)}
								</div>
							)}
							renderInput={params => (
								<TextField
									{...params}
									autoFocus
									label={i18n.t("linkClienteModal.searchLabel")}
									variant="outlined"
									onChange={event => setSearchParam(event.target.value || "")}
									helperText={i18n.t("linkClienteModal.searchHelper")}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{loading ? (
													<CircularProgress color="inherit" size={20} />
												) : null}
												{params.InputProps.endAdornment}
											</>
										)
									}}
								/>
							)}
						/>
					</Box>
					<Tooltip title={i18n.t("linkClienteModal.newCliente")}>
						{/* span: o Tooltip do MUI v4 nao escuta eventos de botao desabilitado */}
						<span>
							<IconButton
								color="primary"
								onClick={() => setQuickCreateOpen(true)}
								disabled={saving}
								style={{ marginTop: 8 }}
							>
								<AddCircleOutlineIcon />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					{i18n.t("linkClienteModal.buttons.cancel")}
				</Button>
				<Button
					color="primary"
					variant="contained"
					onClick={handleLink}
					disabled={!selectedCliente || saving}
				>
					{i18n.t("linkClienteModal.buttons.link")}
				</Button>
			</DialogActions>
		</Dialog>

		{/* Irmao do Dialog: dentro do DialogContent o ESC do quick create
		    fecharia tambem o modal de vinculo */}
		<ClienteQuickCreateModal
			open={quickCreateOpen}
			onClose={() => setQuickCreateOpen(false)}
			onSave={handleClienteCreated}
			initialName={searchParam}
		/>
		</>
	);
};

export default LinkClienteModal;
